import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createTableNodeWithDimensions, INSERT_TABLE_COMMAND, TableNode } from '@lexical/table'
import type { LexicalCommand, LexicalEditor } from 'lexical'
import { $insertNodes, COMMAND_PRIORITY_EDITOR, createCommand } from 'lexical'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'

import { DialogActions } from '../ui/dialog'
import { Button } from '@/components/ui/button'

export const INSERT_NEW_TABLE_COMMAND: LexicalCommand<{
  columns: string;
  rows: string;
  includeHeaders?: boolean;
}> = createCommand('INSERT_NEW_TABLE_COMMAND')

export default function invariant(cond: any, _message: string, ..._args: any[]): void {
  if (cond) {
    return
  }

  throw new Error(
    'Internal Lexical error: invariant() is meant to be replaced at compile ' +
    'time. There is no runtime version.'
  )
}

type CellContextType = {
  cellEditorConfig: any | null;
  cellEditorPlugins: React.ReactNode | null;
  set: (cellEditorConfig: any, cellEditorPlugins: React.ReactNode) => void;
};

export const CellContext = createContext<CellContextType>({
  cellEditorConfig: null,
  cellEditorPlugins: null,
  set: () => {
    // Empty
  }
})

export function TableContext({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [contextValue, setContextValue] = useState<{
    cellEditorConfig: any | null;
    cellEditorPlugins: React.ReactNode | null;
  }>({
    cellEditorConfig: null,
    cellEditorPlugins: null
  })
  return (
    <CellContext.Provider
      value={useMemo(
        () => ({
          cellEditorConfig: contextValue.cellEditorConfig,
          cellEditorPlugins: contextValue.cellEditorPlugins,
          set: (cellEditorConfig: any, cellEditorPlugins: React.ReactNode) => {
            setContextValue({ cellEditorConfig, cellEditorPlugins })
          }
        }),
        [contextValue.cellEditorConfig, contextValue.cellEditorPlugins]
      )}
    >
      {children}
    </CellContext.Provider>
  )
}

export function InsertTableDialog({ editor, onClose }: { editor: LexicalEditor; onClose: () => void }): React.JSX.Element {
  const [rows, setRows] = useState('5')
  const [columns, setColumns] = useState('5')
  const [isDisabled, setIsDisabled] = useState(true)

  useEffect(() => {
    const row = Number(rows)
    const column = Number(columns)
    if (row && row > 0 && row <= 500 && column && column > 0 && column <= 50) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [rows, columns])

  const onClick = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns,
      rows
    })

    onClose()
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex gap-4'>
        <div>
          <label className='text-sm font-medium text-gray-700'>Rows</label>
          <input
            placeholder={'# of rows (1-500)'}
            onChange={(e) => setRows(e.target.value)}
            value={rows}
            data-test-id='table-modal-rows'
            type='number'
            className='mt-1 focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 outline-none'
          />
        </div>

        <div>
          <label className='text-sm font-medium text-gray-700'>Columns</label>
          <input
            placeholder={'# of columns (1-50)'}
            onChange={(e) => setColumns(e.target.value)}
            value={columns}
            data-test-id='table-modal-columns'
            type='number'
            className='mt-1 focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 outline-none'
          />
        </div>
      </div>

      <DialogActions>
        <Button
          disabled={isDisabled}
          onClick={onClick}
          className="ml-auto"
        >
          Confirm
        </Button>
      </DialogActions>
    </div>
  )
}

export function TablePlugin({ cellEditorConfig, children }: { cellEditorConfig: any; children: React.ReactNode }): null {
  const [editor] = useLexicalComposerContext()
  const cellContext = useContext(CellContext)

  useEffect(() => {
    if (!editor.hasNodes([TableNode])) {
      invariant(false, 'TablePlugin: TableNode is not registered on editor')
    }

    cellContext.set(cellEditorConfig, children)

    return editor.registerCommand(
      INSERT_NEW_TABLE_COMMAND,
      ({ columns, rows, includeHeaders }) => {
        const tableNode = $createTableNodeWithDimensions(
          Number(rows),
          Number(columns),
          includeHeaders
        )
        $insertNodes([tableNode])
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [cellContext, cellEditorConfig, children, editor])

  return null
}
